import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { claimDemoMaturaSession } from "../matura/demoMaturaSnapshots";
import { migrateGuestMaturaDraftsToUser } from "../utils/localLearningDrafts";

export const AuthContext = createContext();

// DEV: front na :3000 -> API na :3001
const isBrowser = typeof window !== "undefined";
const onLocal3000 =
  isBrowser &&
  window.location.hostname === "localhost" &&
  window.location.port === "3000";

// Pozwól nadpisać przez REACT_APP_API_URL (CRA)
const ENV_API = isBrowser ? process.env.REACT_APP_API_URL : null;

// W DEV domyślnie łączymy do :3001, w PROD zostawiamy ten sam origin ("")
const API_BASE = ENV_API || (onLocal3000 ? "http://localhost:3001" : "");
const VISITOR_KEY = "mt_visitor_id";

// Sklejacz URL-i API
function withApiBase(url) {
  if (!url) return API_BASE;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (!url.startsWith("/")) return API_BASE + "/" + url;
  return API_BASE + url;
}

function getOrCreateVisitorId() {
  if (!isBrowser) return null;
  let id = window.localStorage.getItem(VISITOR_KEY);
  if (!id) {
    id =
      window.crypto && typeof window.crypto.randomUUID === "function"
        ? window.crypto.randomUUID()
        : `${Date.now()}_${Math.random().toString(16).slice(2)}`;
    window.localStorage.setItem(VISITOR_KEY, id);
  }
  return id;
}

async function readJsonSafe(res) {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

function normalizeApiError(res, json) {
  // Backend zwraca { error: "..." } lub tekst statusText
  const err = json?.error || res.statusText || "Błąd";
  return typeof err === "string" ? err : "Błąd";
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [audit, setAudit] = useState(null);
  const [isAuthReady, setIsAuthReady] = useState(false);

  // 🔥 globalny stan dostępu do kursu i subskrypcji
  const [courseAccessMode, setCourseAccessMode] = useState("open"); // "open" | "subscription"
  const [isCourseAccessModeReady, setIsCourseAccessModeReady] = useState(false);
  const [narratorMode, setNarratorMode] = useState("off"); // "off" | "admin" | "all"
  const [voiceChannelMode, setVoiceChannelMode] = useState("admin"); // "off" | "admin" | "all"
  const [learningPathMode, setLearningPathMode] = useState("off"); // "off" | "admin" | "all"
  const [entitlements, setEntitlements] = useState(null); // dane z /api/payments/access/my
  const [isEntitlementsReady, setIsEntitlementsReady] = useState(false);

  // Uniwersalny fetch z ciasteczkami i bazowym URL-em
  const authorizedFetch = useCallback((url, options = {}) => {
    const headers = new Headers(options.headers || {});
    if (isBrowser) {
      const pagePath = `${window.location.pathname || ""}${window.location.search || ""}`.slice(0, 300);
      if (pagePath) headers.set("x-client-path", pagePath);
    }

    return fetch(withApiBase(url), {
      ...options,
      headers,
      credentials: "include",
    });
  }, []);

  // --- Ładowanie informacji o aktualnie zalogowanym użytkowniku ---
  const loadMe = useCallback(async () => {
    try {
      const res = await authorizedFetch("/api/auth/me");
      if (!res.ok) {
        setUser(null);
        setAudit(null);
        setIsCourseAccessModeReady(true);
        return;
      }
      const data = await res.json();
      const u = data.user || data;

      const shownUsername = u?.usernameDisplay || u?.username || null; // display ma pierwszeństwo
      const displayName = shownUsername || u?.email || null;

      // adminLabel: pokaż email + (Nick) w oryginalnej pisowni, jeśli istnieje
      const adminLabel = u?.email
        ? `${u.email}${shownUsername ? ` (${shownUsername})` : ""}`
        : null;

      setIsCourseAccessModeReady(!u);
      setUser(u ? { ...u, displayName, adminLabel } : null);
      setAudit(data?.audit?.active ? data.audit : null);

    } catch (err) {
      console.error("[AuthContext] /api/auth/me error:", err);
      // bez setUser(null)
    } finally {
      setIsAuthReady(true);
    }

  }, [authorizedFetch]);

  // --- Ładowanie trybu dostępu do kursu (open/subscription) ---
  const loadCourseAccessMode = useCallback(async () => {
    if (!user) {
      setCourseAccessMode("open");
      setIsCourseAccessModeReady(true);
      return;
    }

    setIsCourseAccessModeReady(false);
    try {
      const res = await authorizedFetch("/api/access/course");
      if (!res.ok) {
        setCourseAccessMode("open");
        return;
      }
      const data = await res.json();
      if (data && typeof data.courseAccessMode === "string") {
        setCourseAccessMode(
          data.courseAccessMode === "subscription" ? "subscription" : "open"
        );
      } else {
        setCourseAccessMode("open");
      }
    } catch (err) {
      console.error("[AuthContext] loadCourseAccessMode error:", err);
      setCourseAccessMode("open");
    } finally {
      setIsCourseAccessModeReady(true);
    }
  }, [authorizedFetch, user]);

  const loadNarratorMode = useCallback(async () => {
    if (!user) {
      setNarratorMode("off");
      setVoiceChannelMode("admin");
      setLearningPathMode("off");
      return;
    }

    try {
      const res = await authorizedFetch("/api/quiz-settings");
      if (!res.ok) {
        setNarratorMode("off");
        setVoiceChannelMode("admin");
        setLearningPathMode("off");
        return;
      }
      const data = await res.json();
      setNarratorMode(["admin", "all"].includes(data?.narratorMode) ? data.narratorMode : "off");
      setVoiceChannelMode(["off", "admin", "all"].includes(data?.voiceChannelMode) ? data.voiceChannelMode : "admin");
      setLearningPathMode(["admin", "all"].includes(data?.learningPathMode) ? data.learningPathMode : "off");
    } catch (err) {
      console.error("[AuthContext] loadNarratorMode error:", err);
      setNarratorMode("off");
      setVoiceChannelMode("admin");
      setLearningPathMode("off");
    }
  }, [authorizedFetch, user]);

  // --- Ładowanie statusu subskrypcji / entitlements ---
  const loadEntitlements = useCallback(async () => {
    if (!user) {
      setEntitlements(null);
      setIsEntitlementsReady(true);
      return null;
    }
    try {
      const res = await authorizedFetch("/api/payments/access/my");
      if (!res.ok) {
        setEntitlements(null);
        return null;
      }
      const data = await res.json();
      setEntitlements(data);
      return data;
    } catch (err) {
      console.error("[AuthContext] loadEntitlements error:", err);
      setEntitlements(null);
      return null;
    } finally {
      setIsEntitlementsReady(true);
    }
  }, [authorizedFetch, user]);

  // --- Inicjalne sprawdzenie sesji po starcie aplikacji ---
  useEffect(() => {
    loadMe();
  }, [loadMe]);

// --- Traffic: zapis "visit" (unikatowe wejścia po visitorId) ---
// Odpalamy raz na start aplikacji. Best-effort: nie blokujemy UI.
useEffect(() => {
  if (!isBrowser) return;

  // stałe ID przeglądarki/urządzenia (localStorage)
  const VISITOR_KEY = "mt_visitor_id";

  const getVisitorId = () => {
    let id = window.localStorage.getItem(VISITOR_KEY);
    if (!id) {
      // w nowych przeglądarkach jest crypto.randomUUID()
      id =
        (window.crypto && typeof window.crypto.randomUUID === "function")
          ? window.crypto.randomUUID()
          : `${Date.now()}_${Math.random().toString(16).slice(2)}`;
      window.localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  };

  const visitorId = getVisitorId();

  fetch(withApiBase("/api/traffic/visit"), {
    method: "POST",
    credentials: "include", // ważne: żeby backend mógł powiązać z req.user (jeśli zalogowany)
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      visitorId,
      path: window.location.pathname,
    }),
  }).catch(() => {});
}, []);


  // --- Gdy user się zmienia (login / logout) – dociągamy tryb i subskrypcję ---
  useEffect(() => {
    if (!user) {
      setEntitlements(null);
      setIsEntitlementsReady(true);
      setAudit(null);
      setCourseAccessMode("open");
      setVoiceChannelMode("admin");
      setLearningPathMode("off");
      setIsCourseAccessModeReady(true);
      return;
    }
    setIsEntitlementsReady(false);
    loadCourseAccessMode();
    loadNarratorMode();
    loadEntitlements();
  }, [user, loadCourseAccessMode, loadNarratorMode, loadEntitlements]);

  // Dostęp może zostać aktywowany, wygasnąć albo zostać zmieniony w innej
  // karcie. Odświeżamy go po powrocie do aplikacji i lekko co minutę, aby
  // otwarty frontend nie opierał się na starym stanie do twardego reloadu.
  useEffect(() => {
    if (!user || !isBrowser) return undefined;

    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") loadEntitlements();
    };
    const refreshOnFocus = () => loadEntitlements();
    const interval = window.setInterval(loadEntitlements, 60_000);

    document.addEventListener("visibilitychange", refreshWhenVisible);
    window.addEventListener("focus", refreshOnFocus);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
      window.removeEventListener("focus", refreshOnFocus);
    };
  }, [loadEntitlements, user]);

  useEffect(() => {
    if (!user?.id || user?.role !== "student") return;
    migrateGuestMaturaDraftsToUser(user.email);
    claimDemoMaturaSession(authorizedFetch, user.email).catch(() => {});
  }, [authorizedFetch, user?.email, user?.id, user?.role]);

    // --- Heartbeat: lekki ping co 60s, żeby odświeżać last_seen ---
  useEffect(() => {
    if (!user) return;

  const tick = async () => {
    try {
      const res = await authorizedFetch("/api/auth/me");
      if (res.status === 401) {
        setUser(null);
        setAudit(null);
        setEntitlements(null);
        setCourseAccessMode("open");
        setIsCourseAccessModeReady(true);
        window.location.assign("/login?reason=session_expired");
      }
    } catch {}
  };


    // opcjonalnie: pierwszy ping od razu po wejściu (żeby nie czekać 60s)
    tick();

    const interval = setInterval(tick, 60_000);
    return () => clearInterval(interval);
  }, [user, authorizedFetch]);


  // --- Akcje: login / logout ---

  const login = async (email, password) => {
    try {
      const res = await fetch(withApiBase("/api/auth/login"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          password,
          visitorId: getOrCreateVisitorId(),
          path: isBrowser ? window.location.pathname + window.location.search : "/register",
        }),
      });

      const json = await readJsonSafe(res);

      if (!res.ok) {
        // jeśli backend zwróci EMAIL_NOT_VERIFIED, UI może to obsłużyć w LoginPage
        return { ok: false, error: normalizeApiError(res, json), data: json };
      }

      await loadMe();
      return { ok: true };
    } catch (err) {
      console.error("[AuthContext] login error:", err);
      return { ok: false, error: "Błąd sieci" };
    }
  };

  const logout = async () => {
    try {
      await fetch(withApiBase("/api/auth/logout"), {
        method: "POST",
        credentials: "include",
      });
    } catch (err) {
      console.error("[AuthContext] logout error:", err);
    } finally {
      setUser(null);
      setAudit(null);
      setEntitlements(null);
      setCourseAccessMode("open");
      setIsCourseAccessModeReady(false);
      setIsAuthReady(false);
      loadMe();
    }
  };

  // --- Rejestracja + email verification ---

  // register: tworzy konto i wysyła kod, NIE loguje
  const register = async (email, password) => {
    try {
      const res = await fetch(withApiBase("/api/auth/register"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          password,
          visitorId: getOrCreateVisitorId(),
          path: isBrowser ? window.location.pathname + window.location.search : "/register",
        }),
      });
      const json = await readJsonSafe(res);

      if (!res.ok) {
        return { ok: false, error: normalizeApiError(res, json), data: json };
      }

      return { ok: true, data: json };
    } catch (err) {
      console.error("[AuthContext] register error:", err);
      return { ok: false, error: "Błąd sieci" };
    }
  };

  const verifyEmail = async (email, code) => {
    try {
      const res = await fetch(withApiBase("/api/auth/verify-email"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          code,
          visitorId: getOrCreateVisitorId(),
          path: isBrowser ? window.location.pathname + window.location.search : "/verify-email",
        }),
      });
      const json = await readJsonSafe(res);

      if (!res.ok) {
        return { ok: false, error: normalizeApiError(res, json), data: json };
      }

      // backend po verify robi auto-login (cookie session), więc dociągamy /me
      await loadMe();
      return { ok: true, data: json };
    } catch (err) {
      console.error("[AuthContext] verifyEmail error:", err);
      return { ok: false, error: "Błąd sieci" };
    }
  };

  const resendCode = async (email) => {
    try {
      const res = await fetch(withApiBase("/api/auth/resend-code"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const json = await readJsonSafe(res);

      if (!res.ok) {
        return { ok: false, error: normalizeApiError(res, json), data: json };
      }

      return { ok: true, data: json };
    } catch (err) {
      console.error("[AuthContext] resendCode error:", err);
      return { ok: false, error: "Błąd sieci" };
    }
  };

    // --- Zmiana emaila (po zalogowaniu) ---

  const requestEmailChange = async (newEmail, currentPassword) => {
    try {
      const res = await fetch(withApiBase("/api/auth/email-change/request"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ newEmail, currentPassword }),
      });

      const json = await readJsonSafe(res);

      if (!res.ok) {
        return { ok: false, error: normalizeApiError(res, json), data: json };
      }

      return { ok: true, data: json };
    } catch (err) {
      console.error("[AuthContext] requestEmailChange error:", err);
      return { ok: false, error: "Błąd sieci" };
    }
  };

  const verifyEmailChange = async (code) => {
    try {
      const res = await fetch(withApiBase("/api/auth/email-change/verify"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      });

      const json = await readJsonSafe(res);

      if (!res.ok) {
        return { ok: false, error: normalizeApiError(res, json), data: json };
      }

      // email zmieniony -> odśwież /me
      await loadMe();
      return { ok: true, data: json };
    } catch (err) {
      console.error("[AuthContext] verifyEmailChange error:", err);
      return { ok: false, error: "Błąd sieci" };
    }
  };

  const resendEmailChangeCode = async () => {
    try {
      const res = await fetch(withApiBase("/api/auth/email-change/resend"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });

      const json = await readJsonSafe(res);

      if (!res.ok) {
        return { ok: false, error: normalizeApiError(res, json), data: json };
      }

      return { ok: true, data: json };
    } catch (err) {
      console.error("[AuthContext] resendEmailChangeCode error:", err);
      return { ok: false, error: "Błąd sieci" };
    }
  };

  const setUsername = async (username) => {
    try {
      const res = await fetch(withApiBase("/api/auth/username"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username }),
      });

      const json = await readJsonSafe(res);

      if (!res.ok) {
        return { ok: false, error: normalizeApiError(res, json), data: json };
      }

      // backend zwraca user -> i tak robimy loadMe, żeby mieć spójny stan (displayName/adminLabel)
      await loadMe();
      return { ok: true, data: json };
    } catch (err) {
      console.error("[AuthContext] setUsername error:", err);
      return { ok: false, error: "Błąd sieci" };
    }
  };



  const loginDirect = (userData) => {
    const u = userData || null;
    const shownUsername = u?.usernameDisplay || u?.username || null;
    const displayName = shownUsername || u?.email || null;
    const adminLabel = u?.email
  ? `${u.email}${shownUsername ? ` (${shownUsername})` : ""}`
  : null;

    setUser(u ? { ...u, displayName, adminLabel } : null);
    setAudit(userData?.audit?.active ? userData.audit : null);

    setIsAuthReady(true);
    loadCourseAccessMode();
    loadNarratorMode();
    loadEntitlements();
    return true;
  };

  // pomocnicze funkcje do ręcznego odświeżenia z komponentów
  const reloadAccess = useCallback(async () => {
    return loadEntitlements();
  }, [loadEntitlements]);

  const reloadCourseAccessMode = useCallback(async () => {
    await loadCourseAccessMode();
  }, [loadCourseAccessMode]);

  const reloadNarratorMode = useCallback(async () => {
    await loadNarratorMode();
  }, [loadNarratorMode]);

  const endAudit = async () => {
    try {
      const res = await fetch(withApiBase("/api/auth/impersonation/end"), {
        method: "POST",
        credentials: "include",
      });
      const json = await readJsonSafe(res);
      if (!res.ok) {
        return { ok: false, error: normalizeApiError(res, json), data: json };
      }
      setAudit(null);
      await loadMe();
      return { ok: true, data: json };
    } catch (err) {
      console.error("[AuthContext] endAudit error:", err);
      return { ok: false, error: "BĹ‚Ä…d sieci" };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        audit,
        isAuthReady,
        login,
        logout,
        authorizedFetch,
        loginDirect,
        // registration
        register,
        verifyEmail,
        resendCode,
        // globalny dostęp / subskrypcja
        courseAccessMode,
        isCourseAccessModeReady,
        narratorMode,
        voiceChannelMode,
        learningPathMode,
        entitlements,
        isEntitlementsReady,
        reloadAccess,
        reloadCourseAccessMode,
        reloadNarratorMode,
        endAudit,

        requestEmailChange,
        verifyEmailChange,
        resendEmailChangeCode,
        setUsername,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
